import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { ProductCard } from "@/components/ProductCard";

export default function Wishlist() {
  const { user } = useAuth();
  const { ids } = useWishlist();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api
      .get("/wishlist")
      .then((r) => setProducts(r.data.products))
      .catch((e) => logError("Wishlist page load failed", e))
      .finally(() => setLoading(false));
  }, [user, ids.size]);

  if (user === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  if (!user)
    return (
      <div className="mx-auto max-w-md px-5 py-24 text-center" data-testid="wishlist-signed-out">
        <Heart className="mx-auto h-12 w-12 text-clay" />
        <h1 className="mt-4 font-display text-4xl font-semibold text-forest">Your favourites</h1>
        <p className="mt-2 text-muted-foreground">Sign in to save products you love and find them here later.</p>
        <Link to="/account" className="mt-6 inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-sm font-semibold text-white hover:bg-[#A3161F]">
          <LogIn className="h-4 w-4" /> Sign in
        </Link>
      </div>
    );

  return (
    <div data-testid="wishlist-page" className="mx-auto max-w-[1440px] px-5 py-12 lg:px-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="label-eyebrow text-clay">Saved for later</span>
        <h1 className="mt-2 font-display text-5xl font-semibold tracking-tight text-forest lg:text-6xl">Your favourites</h1>
        <p className="mt-3 text-muted-foreground">{products.length} item{products.length !== 1 ? "s" : ""} saved</p>
      </motion.div>

      {loading ? (
        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-[1.75rem] bg-muted" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="mt-16 text-center" data-testid="wishlist-empty">
          <Heart className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 font-display text-3xl text-forest">No favourites yet</p>
          <p className="mt-2 text-muted-foreground">Tap the heart on any product to save it here.</p>
          <Link to="/shop" className="mt-6 inline-block rounded-full bg-clay px-6 py-3 text-sm font-semibold text-white hover:bg-[#A3161F]">Browse the mart</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
